import React, { useMemo } from "react";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { AccordionStepper, AccordionStepperPane } from "fond/widgets";

import ModificationsPanel from "./ModificationsPanel";
import SettingsPanel from "./SettingsPanel";

interface DataPrepPanelProps {
  project: Project;
  versionId: string;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ project, versionId, readOnly }: DataPrepPanelProps) => {
  const { workflowState: stepOneWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.ISG.DATA_PREP_1,
  });
  const { workflowState: stepTwoWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.ISG.DATA_PREP_2,
  });
  const isInProgress =
    stepOneWorkflow === "STARTING" || stepOneWorkflow === "IN_PROGRESS" || stepTwoWorkflow === "STARTING" || stepTwoWorkflow === "IN_PROGRESS";

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Generate UG Network",
        id: "ugNetwork",
        content: <SettingsPanel isReadOnly={readOnly} canRun={!isInProgress} workflowKey={WORKFLOW_KEYS.ISG.DATA_PREP_1} />,
        isComplete: stepOneWorkflow === "COMPLETE",
      },
      {
        header: "UG Modifications",
        id: "ugModifications",
        content: <ModificationsPanel mode="inUnderground" />,
        isComplete: false,
        optional: true,
      },
      {
        header: "Generate Lead-Ins",
        id: "leadIns",
        content: <SettingsPanel isReadOnly={readOnly} canRun={!isInProgress} workflowKey={WORKFLOW_KEYS.ISG.DATA_PREP_2} />,
        isComplete: stepTwoWorkflow === "COMPLETE",
      },
      {
        header: "Lead-Ins Modifications",
        id: "leadInsModifications",
        content: <ModificationsPanel mode="inLeadIns" />,
        isComplete: false,
        optional: true,
      },
    ],
    [readOnly, stepOneWorkflow, stepTwoWorkflow]
  );

  return <AccordionStepper id={`${project.ID}-data-prep`} panes={accordionPanes} autoMove={[""]} variant="outlined" />;
};

export default DataPrepPanel;
